import { computed, ref } from 'vue';

/**
 * Shared offline / "TripWorks unreachable" state for the whole app (TRIP-2666).
 *
 * `isOffline` is a single module-level ref so the nav indicator
 * (OfflineNavIndicator.vue), the global axios interceptor (offlineInterceptor.js)
 * and every adopted screen agree on one source of truth.
 *
 * Two signals drive it:
 *   1. The browser `offline` event → we're definitely offline. Set immediately.
 *   2. Request outcomes, observed centrally by offlineInterceptor.js —
 *      `markUnreachable()` on a connectivity error (no response / network
 *      error / timeout / 502/503/504) and `markReachable()` on any successful
 *      same-origin response.
 *
 * We intentionally do NOT clear the state on the browser `online` event:
 * regaining a network connection does not prove TripWorks itself is reachable
 * (AC6 requires returning to live only once we can actually reach the server).
 * offlineInterceptor.js listens for `online` and fires a lightweight probe;
 * its success flows through the response interceptor and calls
 * `markReachable()`, which is the authoritative "we're back" signal.
 *
 * Feature flag: the `offline_mode` beta flag is mirrored to localStorage
 * (`offline_mode_enabled`, synced from app_init in App.vue) so it is readable
 * synchronously during an offline reload, when app_init cannot be fetched.
 * The flag gates every ENTRY POINT into the offline state (initial value, the
 * `offline` event listener, the interceptor, `shouldEnterOfflineMode`) rather
 * than the pure setters below — with the flag off, nothing ever sets
 * `isOffline` and the whole layer is a no-op.
 *
 * `offlineSince` records when we went offline this stretch (feeds the nav
 * indicator's "Offline for X" relative time); it's cleared on reconnect.
 */

const FLAG_KEY = 'offline_mode_enabled';

const SIM_KEY = 'tw_offline_sim';

function readFlagMirror() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(FLAG_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

/**
 * Test/QA hook (TRIP-2666): simulate "TripWorks is unreachable" WITHOUT pulling
 * the network in DevTools, so you can drive the whole offline UI (nav bell-swap,
 * read-only treatment, disabled actions) on demand.
 *   - URL:     add `?tw_offline=1` to any page (`?tw_offline=0` turns it off).
 *   - Console: `twSimulateOffline()` / `twSimulateOffline(false)`.
 * The choice is mirrored to sessionStorage so it survives SPA navigation (which
 * drops the query param). While simulating, the state is "sticky": markReachable
 * and a flag-off sync can't clear it, so a successful background request won't
 * silently flip you back online mid-test.
 */
function readOfflineSim() {
  try {
    if (typeof window !== 'undefined' && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('tw_offline')) {
        const v = params.get('tw_offline');
        const on = v !== '0' && v !== 'false';
        sessionStorage.setItem(SIM_KEY, on ? '1' : '0');
        return on;
      }
    }
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SIM_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

let simulating = readOfflineSim();

const offlineModeEnabled = ref(simulating || readFlagMirror());
const isOffline = ref(
  offlineModeEnabled.value && (simulating || (typeof navigator !== 'undefined' && navigator.onLine === false)),
);
// ms epoch of when we first went offline this stretch (null while online). Feeds
// the "Offline for X" relative time in the nav indicator (TRIP-2666).
const offlineSince = ref(isOffline.value ? Date.now() : null);

/**
 * The one combined "the app is offline right now" truth: the feature is on AND
 * we can't reach TripWorks. Everything that reacts to offline — the nav
 * bell-swap (MainMenu*), the `v-requires-connection` directive, and TwButton's
 * `offline-disabled` prop — derives from this single computed so they can never
 * disagree.
 */
export const isAppOffline = computed(() => offlineModeEnabled.value && isOffline.value);

let listenersRegistered = false;

export function isOfflineModeEnabled() {
  return offlineModeEnabled.value;
}

// Pure setters — deliberately NOT flag-gated (the flag gates their callers).
export function markUnreachable() {
  if (offlineSince.value == null) offlineSince.value = Date.now();
  isOffline.value = true;
}

export function markReachable() {
  if (simulating) return; // simulation pins us offline regardless of real traffic
  isOffline.value = false;
  offlineSince.value = null;
}

/**
 * Toggle simulated offline at runtime (see readOfflineSim). Exposed on
 * `window.twSimulateOffline` for console use.
 */
export function simulateOffline(on = true) {
  simulating = !!on;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SIM_KEY, simulating ? '1' : '0');
    }
  } catch (_e) {
    /* sessionStorage unavailable — refs still update for this session */
  }
  if (simulating) {
    offlineModeEnabled.value = true;
    isOffline.value = true;
    if (offlineSince.value == null) offlineSince.value = Date.now();
  } else {
    isOffline.value = false;
    offlineSince.value = null;
  }
}

/** Whether a test/QA offline simulation is currently pinning us offline. */
export function isSimulatingOffline() {
  return simulating;
}

if (typeof window !== 'undefined') {
  window.twSimulateOffline = simulateOffline;
  // Debug snapshot of the offline state machine (TRIP-2666). Run twOfflineState()
  // in the console to see exactly which signal is (or isn't) set.
  window.twOfflineState = () => ({
    offlineModeEnabled: offlineModeEnabled.value,
    isOffline: isOffline.value,
    isAppOffline: isAppOffline.value,
    offlineSince: offlineSince.value,
    simulating,
    flagMirror: (() => {
      try {
        return localStorage.getItem(FLAG_KEY);
      } catch (_e) {
        return 'unreadable';
      }
    })(),
    navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : 'n/a',
  });
}

/**
 * Synced from app_init's `beta_features.offline_mode` in App.vue. Disabling
 * degrades immediately: any offline state and banner context are cleared so
 * the UI returns to normal without a reload.
 */
export function setOfflineModeFlag(enabled) {
  // A live simulation pins the feature on so app_init's real flag value can't
  // turn the test session off mid-flight.
  if (simulating) {
    offlineModeEnabled.value = true;
    return;
  }
  try {
    if (enabled) {
      localStorage.setItem(FLAG_KEY, '1');
    } else {
      localStorage.removeItem(FLAG_KEY);
    }
  } catch (_e) {
    /* localStorage unavailable — ref still updates for this session */
  }
  offlineModeEnabled.value = !!enabled;
  if (!enabled) {
    markReachable();
  }
}

/**
 * True when an axios error means "TripWorks is unreachable": the request never
 * got a response (network down / DNS / timeout) or a gateway-level outage
 * status (502/503/504). A bare 500 is deliberately excluded — it usually means
 * an app bug on an otherwise reachable server, and masking it behind the
 * offline banner would hide the real error from the user.
 * Requires the error to be axios-originated (config/request present) so that a
 * programming error thrown inside a .then() handler is never misclassified as
 * an outage — 4xx and plain JS errors must surface normally.
 * Cancellations (ERR_CANCELED, e.g. route-change aborts) are routine and never
 * an outage signal.
 */
const GATEWAY_OUTAGE_STATUSES = [502, 503, 504];

export function isConnectivityError(error) {
  const axiosOriginated = !!(error && (error.config || error.request || error.isAxiosError));
  if (!axiosOriginated) return false;
  if (error.code === 'ERR_CANCELED') return false;
  if (!error.response) return true;
  return GATEWAY_OUTAGE_STATUSES.includes(error.response.status);
}

/**
 * The one check screens should use before falling back to cached data:
 * "is this error an outage AND is the offline feature enabled for this
 * account?". Note: errors rejected by the offline write-gate
 * (offlineInterceptor.js) intentionally satisfy this too, so a screen's
 * existing outage branch also handles blocked writes gracefully.
 */
export function shouldEnterOfflineMode(error) {
  return isOfflineModeEnabled() && isConnectivityError(error);
}

function registerListeners() {
  if (listenersRegistered || typeof window === 'undefined') return;
  window.addEventListener('offline', () => {
    if (isOfflineModeEnabled()) markUnreachable();
  });
  listenersRegistered = true;

  // Cold-load correction: `isOffline` is seeded once at module-eval, when
  // navigator.onLine can still be racing and any early `offline` event fires
  // before the listener above exists — so a page loaded while ALREADY offline
  // can seed `isOffline=false` with no event to fix it. registerListeners runs
  // at first component use, by which point navigator.onLine has settled, so
  // re-derive the offline truth here. (We only correct the false-negative;
  // returning to live still goes through the reachability probe, not a bare
  // navigator.onLine, per the AC6 contract above.) (TRIP-2666)
  if (isOfflineModeEnabled() && typeof navigator !== 'undefined' && navigator.onLine === false) {
    markUnreachable();
  }
}

export function useOfflineDetection() {
  registerListeners();
  return {
    isOffline,
    isAppOffline,
    offlineModeEnabled,
    offlineSince,
    markUnreachable,
    markReachable,
  };
}
